import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Copy, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { copyToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';

interface DeliveryCodeRowProps {
  /** The dispatch's `validationCode` — shown only for a provider with `DELIVERY_CODE`. */
  code: string;
  /** What the operator does with it (under the row, or the row's tooltip when `compact`). */
  hint?: string;
  /** The board card's size. */
  compact?: boolean;
  className?: string;
}

/**
 * "Código de entrega: 2076901" with a copy button: the code the customer gives the rider at the
 * door (Logística 21). An order's customer gets it by WhatsApp with the tracking link; a "Solo envío"
 * gets no WhatsApp, so the operator relays it. Callers show it only while the run is active.
 */
export function DeliveryCodeRow({ code, hint, compact = false, className }: DeliveryCodeRowProps) {
  const { t } = useTranslation('orders');
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  // The shared helper: `navigator.clipboard` is missing on a non-secure origin and rejects unfocused.
  const copy = async () => {
    if (!(await copyToClipboard(code))) {
      toast.error(t('delivery.section.deliveryCodeCopyFailed'));
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={className}>
      <div
        className={cn('flex items-center gap-1.5', compact ? 'text-[11px]' : 'text-xs')}
        title={compact ? hint : undefined}
      >
        <KeyRound className={cn('shrink-0 text-muted-foreground', compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
        <span className="text-muted-foreground">{t('delivery.section.deliveryCode')}:</span>
        <span className="font-mono font-semibold tracking-wider text-foreground tabular-nums">{code}</span>
        <Button
          variant="ghost"
          size="icon"
          className={cn('shrink-0', compact ? 'h-5 w-5' : 'h-6 w-6')}
          onClick={() => void copy()}
          aria-label={t('delivery.section.deliveryCodeCopy')}
        >
          {copied ? <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> : <Copy className="h-3 w-3" />}
        </Button>
      </div>
      {!compact && hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
